import React, { useEffect, useContext } from "react";
import { css } from "@emotion/css";
import { CSSTransition } from "react-transition-group";

import { IconButton } from "@fluentui/react";
import { useLocation } from "react-router-dom";
import { MaximizerContext } from "../contexts/MaximizerContext";

interface ISectionProps {
  label: string;
  children: React.ReactNode;
}

const styles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: white;
  margin: 8px 0 0 0;

  & .section--button .ms-Icon {
    color: var(--main-bg-color, gray);
  }

  &[data-hidden="true"] {
    visibility: hidden;
  }

  &[data-maximized="true"] {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
  }

  & .section--header {
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: var(--secondary-bg-color, lightgray);
    width: 100%;
    &:last-child {
      align-self: end;
    }
    & h3 {
      cursor: pointer;
      flex-grow: 1;
    }
  }

  & .section--content {
    margin-top: 8px;

    &.enter {
      max-height: 1440px;
      transform: scaleY(1);
      transform-origin: top;
      transition: transform 0.25s ease, max-height 0.25s ease-in, opacity 0.5s;
      opacity: 1;

      &.enter-active {
        transform: scaleY(0);
        opacity: 0;
        max-height: 0;
      }
    }
    &.enter-done {
      height: 0;
      visibility: hidden;
    }

    &.exit {
      transform: scaleY(0);
      transform-origin: top;
      transition: max-height 0.5s ease-out, transform 0.5s ease, opacity 0.5s;
      max-height: 0;
      opacity: 0;

      &.exit-active {
        transform: scaleY(1);
        max-height: 1440px;
        opacity: 1;
        display: block;
      }
    }
  }
`;

const Section: React.FunctionComponent<ISectionProps> = (props: ISectionProps) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const [maximized, setMaximized] = React.useState(false);
  const location = useLocation();
  const { hideFooter, hiddenState } = useContext(MaximizerContext);

  // The section should return to the default state
  // before navigating to another url
  useEffect(() => {
    setMaximized(false);
    hideFooter(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const toggleCollapsed = () => {
    setCollapsed((previousCollapsed: boolean) => !previousCollapsed);
  };

  const toggleMaximize = () => {
    setMaximized((previousMaximized: boolean) => {
      if (!previousMaximized) {
        document.body.scrollTop = 0;
      }
      hideFooter(!previousMaximized);
      return !previousMaximized;
    });
  };

  return (
    <section
      className={styles}
      data-maximized={maximized}
      data-hidden={hiddenState && !maximized}
      aria-label={props.label}
    >
      <div className="section--header">
        <IconButton
          className="section--button"
          iconProps={{ iconName: collapsed ? "ChevronUp" : "ChevronDown" }}
          ariaLabel={collapsed ? "Expand section" : "Collapse section"}
          title={collapsed ? "Expand" : "Collapse"}
          onClick={toggleCollapsed}
        />
        <h3>
          <button
            className="section--header-btn"
            onClick={toggleCollapsed}
            style={{ all: "unset", cursor: "pointer", font: "inherit" }}
          >
            {props.label}
          </button>
        </h3>
        <IconButton
          className="section--button"
          iconProps={{ iconName: maximized ? "BackToWindow" : "Fullscreen" }}
          title={maximized ? "Restore" : "Maximize"}
          onClick={toggleMaximize}
        />
      </div>
      <CSSTransition in={collapsed} timeout={500}>
        <div className="section--content">{props.children}</div>
      </CSSTransition>
    </section>
  );
};

export default Section;
